import api from './api';
import {
  Dashboard,
  CreateDashboardRequest,
  UpdateDashboardRequest,
  PaginatedResponse,
  InputWidgetInfo,
  SharedWidgetInfo,
  DashboardActiveAssignmentsResponse,
  DashboardVersion,
  DashboardVersionDetail,
  DashboardVersionListResponse,
} from '../types';

export interface DashboardFilters {
  search?: string;
  workflowId?: string;
  widgetType?: string;
  tagId?: string;
}

export const dashboardService = {
  getDashboards: async (
    page = 1,
    pageSize = 10,
    filters?: DashboardFilters
  ): Promise<PaginatedResponse<Dashboard>> => {
    const params: Record<string, unknown> = { page, page_size: pageSize };
    if (filters?.search) params.search = filters.search;
    if (filters?.workflowId) params.workflow_id = filters.workflowId;
    if (filters?.widgetType) params.widget_type = filters.widgetType;
    if (filters?.tagId) params.tag_id = filters.tagId;

    const response = await api.get<PaginatedResponse<Dashboard>>('/dashboards', {
      params,
    });
    return response.data;
  },

  getDashboard: async (id: string): Promise<Dashboard> => {
    const response = await api.get<Dashboard>(`/dashboards/${id}`);
    return response.data;
  },

  createDashboard: async (data: CreateDashboardRequest): Promise<Dashboard> => {
    const response = await api.post<Dashboard>('/dashboards', data);
    return response.data;
  },

  updateDashboard: async (
    id: string,
    data: UpdateDashboardRequest & { createNewVersions?: boolean; versionNotes?: string }
  ): Promise<Dashboard> => {
    const response = await api.put<Dashboard>(`/dashboards/${id}`, data);
    return response.data;
  },

  /**
   * Get the count of active assignments for workflows using this dashboard.
   * Used to determine if versioning prompt should be shown when editing.
   */
  getActiveAssignmentsCount: async (id: string): Promise<DashboardActiveAssignmentsResponse> => {
    const response = await api.get<DashboardActiveAssignmentsResponse>(
      `/dashboards/${id}/active-assignments-count`
    );
    return response.data;
  },

  deleteDashboard: async (id: string): Promise<void> => {
    await api.delete(`/dashboards/${id}`);
  },

  duplicateDashboard: async (id: string, newName?: string): Promise<Dashboard> => {
    const response = await api.post<Dashboard>(
      `/dashboards/${id}/duplicate`,
      newName ? { name: newName } : undefined
    );
    return response.data;
  },

  // Get specific widget data from a dashboard (for Dashboard Reference widgets)
  getWidgetData: async (dashboardId: string, dataKey: string): Promise<{ data: unknown; exists: boolean }> => {
    const response = await api.get<{ data: unknown; exists: boolean }>(
      `/dashboards/${dashboardId}/widget-data/${dataKey}`
    );
    return response.data;
  },

  // Update specific widget data on a dashboard (for editable Dashboard Reference widgets)
  updateWidgetData: async (dashboardId: string, dataKey: string, data: unknown): Promise<{ success: boolean; data: unknown }> => {
    const response = await api.patch<{ success: boolean; data: unknown }>(
      `/dashboards/${dashboardId}/widget-data/${dataKey}`,
      { data }
    );
    return response.data;
  },

  // Get list of input widgets from a dashboard (for Dashboard Reference builder)
  getInputWidgets: async (dashboardId: string): Promise<{ widgets: InputWidgetInfo[]; dashboardName: string }> => {
    const response = await api.get<{ widgets: InputWidgetInfo[]; dashboardName: string }>(
      `/dashboards/${dashboardId}/input-widgets`
    );
    return response.data;
  },

  // Get all shared widgets across all dashboards
  getSharedWidgets: async (): Promise<{ sharedWidgets: SharedWidgetInfo[] }> => {
    const response = await api.get<{ sharedWidgets: SharedWidgetInfo[] }>(
      '/dashboards/shared-widgets'
    );
    return response.data;
  },

  // ============== VERSION METHODS ==============

  /**
   * List all versions of a dashboard.
   */
  getDashboardVersions: async (dashboardId: string): Promise<DashboardVersionListResponse> => {
    const response = await api.get<DashboardVersionListResponse>(
      `/dashboards/${dashboardId}/versions`
    );
    return response.data;
  },

  /**
   * Get details of a specific dashboard version including the snapshot data.
   */
  getDashboardVersion: async (
    dashboardId: string,
    versionId: string
  ): Promise<DashboardVersionDetail> => {
    const response = await api.get<DashboardVersionDetail>(
      `/dashboards/${dashboardId}/versions/${versionId}`
    );
    return response.data;
  },

  /**
   * Manually create a new version snapshot of the current dashboard state.
   */
  createDashboardVersion: async (
    dashboardId: string,
    versionLabel?: string
  ): Promise<DashboardVersion> => {
    const response = await api.post<DashboardVersion>(
      `/dashboards/${dashboardId}/versions`,
      null,
      { params: versionLabel ? { version_label: versionLabel } : undefined }
    );
    return response.data;
  },

};
